<template>
  <button
      v-if="hasQuery"
      id="autocomplete-search__clear-button"
      class="autocomplete-search__clear-button"
      type="button"
      :class="{ 'q-input-with-button': showButtonOnInput }"
      :title="clearLabel"
      :aria-label="clearLabel"
      @click.prevent="handleClear"
  >
    <!-- Gmail-like close "X" -->
    <svg class="q-icon q-icon--gmail-x" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
          d="M6 6l12 12M18 6L6 18"
          fill="none"
          stroke="currentColor"
          stroke-width="2.25"
          stroke-linecap="round"
          stroke-linejoin="round"
      />
    </svg>
    <span class="visually-hidden">{{ clearLabel }}</span>
  </button>
</template>

<script setup lang="ts">
import {useUrlStore} from "@/store/SerpPage/UrlStore";
import {useSearchQueryStore} from "@/store/SearchQueryStore";
import {useMobileViewStore} from "@/store/MobileViewStore";
import {useBridgeStore} from "@/store/BridgeStore";
import { computed, defineProps, inject, ref, toRefs, unref } from "vue";
import {storeToRefs} from "pinia";
import type {
  CustomSmartSearchLabels,
  SmartSearchConfigInterface,
  SmartSearchTranslationsInterface
} from "@/core/types";

const props = defineProps<{ autocomplete?: boolean }>();

const urlStore = useUrlStore();
const queryStore = useSearchQueryStore();
const {hasQuery} = toRefs(queryStore);

const bridgeStore = useBridgeStore();
const mobileViewStore = useMobileViewStore();
const {isMobileView} = storeToRefs(mobileViewStore);

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>("SmartSearchConfig")!);
const SmartSearchTranslations = inject('SmartSearchTranslations') as any;
const {showButtonOnInput} = SmartSearchConfig.value.serp;
const {redirectionUrl} = SmartSearchConfig.value.appConfiguration;
const customLabels = (typeof window !== 'undefined'
  ? (window as any)?._QONizer?.customSmartSearchConfig?.labels
  : undefined) as CustomSmartSearchLabels | undefined;

const normalizeLabel = (label: unknown): string | undefined => {
  if (typeof label !== 'string') {
    return undefined;
  }

  const trimmed = label.trim();
  return trimmed ? trimmed : undefined;
};

const clearLabel = computed(() => {
  const customLabel = props.autocomplete
    ? normalizeLabel(customLabels?.autocomplete?.clearQuery)
    : normalizeLabel(customLabels?.serp?.clearQuery);

  if (customLabel) {
    return customLabel;
  }

  const translationCandidates = [
    normalizeLabel(unref(SmartSearchTranslations)?.autocomplete?.clearQuery),
    normalizeLabel(unref(SmartSearchTranslations)?.userRecentQueries?.clearButton),
    normalizeLabel(unref(SmartSearchTranslations)?.filters?.clear)
  ];

  const resolvedTranslation = translationCandidates.find(Boolean);
  if (resolvedTranslation) {
    return resolvedTranslation as string;
  }

  return 'Wyczyść';
});

const focusSearchInput = () => {
  const inputElement = document.getElementById('autocomplete-search__input') as HTMLInputElement | null;

  if (props.autocomplete) {
    if (isMobileView.value) {
      bridgeStore.focusInternalInput();
    } else {
      bridgeStore.focusClientInput();
    }
  } else if (!inputElement) {
    bridgeStore.focusClientInput();
  }

  if (inputElement) {
    window.requestAnimationFrame(() => {
      inputElement.focus();
      if (inputElement.value) {
        inputElement.select();
      }
    });
  }
};

const handleClear = async () => {
  await urlStore.clearQuery(redirectionUrl, Boolean(props.autocomplete));
  focusSearchInput();
};
</script>

<style scoped>
.autocomplete-search__clear-button {
  -webkit-tap-highlight-color: transparent !important;
  appearance: none !important;
  border: 0 !important;
  background: transparent !important;
  padding: 0 !important;

  position: absolute !important;
  inset-inline-end: 20px !important;
  inset-block-start: 50% !important;
  transform: translateY(-50%) !important;
  z-index: 10 !important;

  width: 24px !important;
  height: 24px !important;
  border-radius: 9999px !important;

  display: grid !important;
  place-items: center !important;

  color: #202124 !important; /* Gmail neutral */
  opacity: 0.84 !important;
  cursor: pointer !important;
  outline: none !important;
  transition: background 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease !important;
}

.autocomplete-search__clear-button:hover {
  background: rgba(32, 33, 36, 0.06) !important;
}

.autocomplete-search__clear-button:active {
  background: rgba(32, 33, 36, 0.1) !important;
}

.autocomplete-search__clear-button:focus-visible {
  box-shadow: 0 0 0 2px rgba(32, 33, 36, 0.16) !important;
  background: rgba(32, 33, 36, 0.06) !important;
}

.autocomplete-search__clear-button .q-icon {
  width: 20px !important;
  height: 20px !important;
  display: block !important;
}

.autocomplete-search__clear-button.q-input-with-button {
  inset-inline-end: 80px !important;
}
.autocomplete-search__clear-button:not(.q-input-with-button) {
  inset-inline-end: 20px !important;
}


@media (max-width: 991.98px) {
  .q-autocomplete-active .autocomplete-search__clear-button {
    inset-inline-end: 10px !important;
  }
}

/* A11Y helper */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
